import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, Upload, Loader2, AlertCircle, Building2, User, FileSearch } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { configuracionAPI, facturasAPI } from '../api';
import { toast } from '../hooks/use-toast';

const VACIO = {
  nombre: '', nombre_comercial: '', razon_social: '', nif: '', tipo_persona: 'J',
  direccion: '', codigo_postal: '', poblacion: '', provincia: '', pais: 'España',
  telefono: '', telefono2: '', email: '', web: '',
  iban: '', datos_pago: '', pie_factura: '',
  prefijo_serie: '', series_alias: '',
  arrendamientos_activo: false, serie_arrendamientos: '',
};

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

export const FacturadoresConfig = () => {
  const [facturadores, setFacturadores] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [abierto, setAbierto] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(VACIO);
  const [logoFile, setLogoFile] = useState(null);
  const [previsualizando, setPrevisualizando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await configuracionAPI.getFacturadores(true);
      setFacturadores(r.data || []);
    } catch (e) {
      toast({ title: 'No se pudieron cargar los facturadores', variant: 'destructive' });
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirNuevo = () => {
    setEditando(null);
    setForm(VACIO);
    setLogoFile(null);
    setAbierto(true);
  };

  const abrirEdicion = (f) => {
    setEditando(f);
    setForm({ ...VACIO, ...Object.fromEntries(
      Object.keys(VACIO).map((k) => [k, f[k] === null || f[k] === undefined ? '' : f[k]])
    ), tipo_persona: f.tipo_persona || 'J', pais: f.pais || 'España' });
    setLogoFile(null);
    setAbierto(true);
  };

  const set = (campo) => (e) => setForm((prev) => ({ ...prev, [campo]: e.target.value }));

  const guardar = async () => {
    if (!form.nombre.trim()) {
      toast({ title: 'El nombre de la serie es obligatorio', variant: 'destructive' });
      return;
    }
    setGuardando(true);
    try {
      let id = editando?.id;
      if (editando) {
        await configuracionAPI.updateFacturador(id, form);
      } else {
        const r = await configuracionAPI.createFacturador(form);
        id = r.data?.id;
      }
      if (logoFile && id) {
        const fd = new FormData();
        fd.append('file', logoFile);
        await configuracionAPI.uploadFacturadorLogo(id, fd);
      }
      toast({ title: editando ? 'Facturador actualizado' : 'Facturador creado' });
      setAbierto(false);
      await cargar();
    } catch (e) {
      toast({
        title: 'Error al guardar',
        description: e?.response?.data?.detail || 'Revisa los datos e inténtalo de nuevo',
        variant: 'destructive',
      });
    } finally {
      setGuardando(false);
    }
  };

  const verComoQueda = async () => {
    setPrevisualizando(true);
    try {
      const res = await facturasAPI.previewEmisorPdf({
        ...form,
        facturador_id: editando?.id || null,
      });
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: 'application/pdf' })
      );
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast({
        title: 'No se pudo generar la vista previa',
        description: e?.response?.data?.detail || 'Inténtalo de nuevo',
        variant: 'destructive',
      });
    } finally {
      setPrevisualizando(false);
    }
  };

  const cambiarActivo = async (f, activo) => {
    try {
      await configuracionAPI.toggleFacturadorActivo(f.id, activo);
      toast({ title: activo ? `${f.nombre} activado` : `${f.nombre} desactivado` });
      await cargar();
    } catch (e) {
      toast({ title: 'No se pudo cambiar el estado', variant: 'destructive' });
    }
  };

  const borrar = async (f) => {
    if (!window.confirm(
      `¿Eliminar el facturador "${f.nombre}"?\n\n` +
      'Si tiene facturas emitidas no se borrará: se desactivará para que no aparezca en las facturas nuevas.'
    )) return;
    try {
      const r = await configuracionAPI.deleteFacturador(f.id);
      toast({ title: r.data?.message || 'Facturador eliminado' });
      await cargar();
    } catch (e) {
      toast({ title: 'No se pudo eliminar', variant: 'destructive' });
    }
  };

  const campo = (etiqueta, clave, extra = {}) => (
    <div>
      <Label className="text-xs">{etiqueta}</Label>
      <Input
        value={form[clave]}
        onChange={set(clave)}
        data-testid={`facturador-input-${clave}`}
        {...extra}
      />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="facturadores-config">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-gray-600">
          Datos de emisión que aparecen en las facturas (pantalla y PDF). El campo
          <strong> Serie</strong> debe coincidir con la serie de las facturas; en
          <strong> Otras series</strong> puedes añadir las históricas separadas por punto y coma.
        </p>
        <Button onClick={abrirNuevo} data-testid="facturador-nuevo-btn">
          <Plus className="w-4 h-4 mr-2" /> Nuevo facturador
        </Button>
      </div>

      <div className="border rounded-lg">
        {cargando ? (
          <p className="p-6 text-center text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Cargando...
          </p>
        ) : facturadores.length === 0 ? (
          <p className="p-4 text-center text-gray-500">No hay facturadores configurados</p>
        ) : (
          <div className="divide-y">
            {facturadores.map((f) => {
              const faltan = f.campos_incompletos || [];
              return (
                <div
                  key={f.id}
                  className="flex items-start justify-between gap-4 p-3 hover:bg-gray-50 transition-colors"
                  data-testid={`facturador-fila-${f.id}`}
                >
                  <div className="flex items-start gap-3 min-w-0">
                    {f.has_logo ? (
                      <img
                        src={`${BACKEND}/api/configuracion/facturadores/${f.id}/logo`}
                        alt=""
                        className="w-12 h-12 object-contain rounded border bg-white"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded border bg-gray-50 flex items-center justify-center text-gray-400">
                        {f.tipo_persona === 'F' ? <User className="w-5 h-5" /> : <Building2 className="w-5 h-5" />}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{f.nombre_comercial || f.nombre}</span>
                        {f.nif && <Badge variant="outline">{f.nif}</Badge>}
                        {f.prefijo_serie && <Badge variant="secondary">/{f.prefijo_serie}</Badge>}
                        {f.arrendamientos_activo && (
                          <Badge className="bg-emerald-100 text-emerald-800"
                                 data-testid={`facturador-arrendamientos-${f.id}`}>
                            Arrendamientos · {f.serie_arrendamientos || 'ARR'}
                          </Badge>
                        )}
                        {!f.activo && <Badge className="bg-gray-200 text-gray-700">Desactivado</Badge>}
                      </div>
                      <p className="text-xs text-gray-500 truncate">
                        {[f.direccion, [f.codigo_postal, f.poblacion].filter(Boolean).join(' '), f.provincia]
                          .filter(Boolean).join(' · ') || 'Sin dirección fiscal'}
                      </p>
                      <p className="text-xs text-gray-400 truncate">
                        Serie: {f.nombre}{f.series_alias ? ` · Otras: ${f.series_alias}` : ''}
                      </p>
                      {faltan.length > 0 && (
                        <p className="text-xs text-amber-600 flex items-center gap-1 mt-1">
                          <AlertCircle className="w-3 h-3" /> Faltan datos fiscales: {faltan.join(', ')}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Switch
                      checked={!!f.activo}
                      onCheckedChange={(v) => cambiarActivo(f, v)}
                      data-testid={`facturador-activo-${f.id}`}
                    />
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => abrirEdicion(f)}
                      data-testid={`facturador-editar-${f.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost" size="sm" className="text-red-600"
                      onClick={() => borrar(f)}
                      data-testid={`facturador-borrar-${f.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {abierto && (
        <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50/40" data-testid="facturador-form">
          <p className="font-semibold mb-4">
            {editando ? `Editar ${editando.nombre}` : 'Nuevo facturador'}
          </p>

          <div className="space-y-5">
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Identificación</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {campo('Nombre comercial (rótulo de la factura)', 'nombre_comercial', { placeholder: 'lex duo' })}
                {campo('Razón social / nombre fiscal', 'razon_social')}
                {campo('NIF / CIF', 'nif')}
                <div>
                  <Label className="text-xs">Tipo</Label>
                  <Select
                    value={form.tipo_persona}
                    onValueChange={(v) => setForm((p) => ({ ...p, tipo_persona: v }))}
                  >
                    <SelectTrigger data-testid="facturador-input-tipo_persona">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="J">Persona jurídica (sociedad)</SelectItem>
                      <SelectItem value="F">Persona física</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Domicilio fiscal</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  {campo('Dirección', 'direccion', { placeholder: 'C. Bernat Bacià, 6 entresol 1' })}
                </div>
                {campo('Código postal', 'codigo_postal')}
                {campo('Población', 'poblacion')}
                {campo('Provincia', 'provincia')}
                {campo('País', 'pais')}
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Contacto</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {campo('Teléfono', 'telefono')}
                {campo('Teléfono 2', 'telefono2')}
                {campo('Email', 'email')}
                {campo('Web', 'web')}
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Cobro y pie de factura</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {campo('IBAN', 'iban')}
                {campo('Datos de pago (titular, banco...)', 'datos_pago')}
              </div>
              <div className="mt-3">
                <Label className="text-xs">Texto libre al pie de la factura</Label>
                <Textarea
                  rows={3}
                  value={form.pie_factura}
                  onChange={set('pie_factura')}
                  data-testid="facturador-input-pie_factura"
                />
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Series y numeración</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {campo('Serie (nombre exacto)', 'nombre', { placeholder: 'LEXDUO ADVOCATS SLP' })}
                {campo('Sufijo del número', 'prefijo_serie', { placeholder: 'EC' })}
                {campo('Otras series (;)', 'series_alias', { placeholder: 'EMPRESA; LEXDUO' })}
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                Actividades económicas
              </p>
              <div className="flex items-start gap-3 p-3 bg-white border rounded-md">
                <Switch
                  checked={!!form.arrendamientos_activo}
                  onCheckedChange={(v) => setForm((p) => ({
                    ...p,
                    arrendamientos_activo: v,
                    serie_arrendamientos: v ? (p.serie_arrendamientos || 'ARR') : p.serie_arrendamientos,
                  }))}
                  data-testid="facturador-arrendamientos-switch"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium">Arrendamiento de inmuebles</p>
                  <p className="text-xs text-gray-500">
                    Además de la actividad profesional, este facturador alquila inmuebles. Las
                    facturas de alquiler llevan su propia serie, no entran en el modelo 130 y en el
                    303 se ven desglosadas aparte.
                  </p>
                  {form.arrendamientos_activo && (
                    <div className="mt-3 max-w-xs">
                      <Label className="text-xs">Prefijo de la serie de alquileres</Label>
                      <Input
                        value={form.serie_arrendamientos}
                        onChange={(e) => setForm((prev) => ({
                          ...prev, serie_arrendamientos: e.target.value.toUpperCase(),
                        }))}
                        placeholder="ARR"
                        data-testid="facturador-input-serie_arrendamientos"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Los números saldrán como {(form.serie_arrendamientos || 'ARR')}-
                        {new Date().getFullYear()}-0001. Usa un prefijo distinto para cada facturador.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Logo</p>
              <div className="flex items-center gap-3">
                {editando?.has_logo && (
                  <img
                    src={`${BACKEND}/api/configuracion/facturadores/${editando.id}/logo`}
                    alt=""
                    className="w-20 h-16 object-contain border rounded bg-white"
                  />
                )}
                <label className="inline-flex items-center gap-2 text-sm border rounded-md px-3 py-2 cursor-pointer hover:bg-gray-50">
                  <Upload className="w-4 h-4" />
                  {logoFile ? logoFile.name : 'Seleccionar imagen (png/jpg)'}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                    data-testid="facturador-input-logo"
                  />
                </label>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-5">
            <Button
              variant="outline"
              onClick={verComoQueda}
              disabled={previsualizando}
              data-testid="facturador-preview-btn"
            >
              {previsualizando
                ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                : <FileSearch className="w-4 h-4 mr-2" />}
              Ver cómo queda
            </Button>
            <Button variant="outline" onClick={() => setAbierto(false)} data-testid="facturador-cancelar-btn">
              Cancelar
            </Button>
            <Button onClick={guardar} disabled={guardando} data-testid="facturador-guardar-btn">
              {guardando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Guardar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FacturadoresConfig;
